import React, { useState, useEffect } from 'react';
import { FiBookmark, FiSearch, FiFilter, FiUser, FiImage, FiDownload, FiCopy, FiEye } from 'react-icons/fi';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import showToast from '../../components/Toast';
import { usePermissions } from '../../contexts/PermissionContext';
import './Library.css';

function Library() {
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState('media');
  const [libraryData, setLibraryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const BASE_URL = config.api.baseURL + '/' + config.api.apiPath;

  // Fetch library data
  const fetchLibraryData = async (page = 1, search = '') => {
    // Check if user has permission to view library
    if (!hasPermission('canViewLibrary')) {
      setLoading(false);
      showToast.error('You do not have permission to view library');
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      if (search) {
        params.append('search', search);
      }

      const response = await AxiosInstance.get(`${BASE_URL}/library/images?${params}`);
      
      if (response.data && response.data.statusCode === 200) {
        setLibraryData(response.data.data);
        setTotalPages(response.data.data.pagination.pages);
        setCurrentPage(response.data.data.pagination.page);
      }
    } catch (error) {
      console.error('Error fetching library data:', error);
      showToast.error('Failed to fetch library data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLibraryData();
  }, []);

  // Handle search
  const handleSearch = () => {
    if (!hasPermission('canViewLibrary')) {
      showToast.error('You do not have permission to search library');
      return;
    }
    fetchLibraryData(1, searchTerm);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    if (!hasPermission('canViewLibrary')) {
      showToast.error('You do not have permission to view library');
      return;
    }
    fetchLibraryData(page, searchTerm);
  };

  // Copy image URL
  const copyImageUrl = (url) => {
    if (!hasPermission('canViewLibrary')) {
      showToast.error('You do not have permission to copy library URLs');
      return;
    }
    const fullUrl = `${config.api.baseURL}${url}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      showToast.success('Image URL copied to clipboard!');
    });
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Check permission before rendering
  if (!hasPermission('canViewLibrary')) {
    return (
      <div className="page-main">
        <div className="empty-state">
          <FiBookmark className="empty-icon" />
          <h3>Access Denied</h3>
          <p>You do not have permission to view the library. Contact your administrator for access.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-main">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-main">
      <div className="page-title-row">
        <h1><FiBookmark className="page-title-icon" size={28} />{" "}Library</h1>
      </div>

      {/* Search and Filters */}
      <div className="library-search-section">
        <div className="search-bar">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search images by tags, description, or filename..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button onClick={handleSearch}>Search</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="library-tabs">
        <button 
          className={activeTab === 'media' ? 'active' : ''}
          onClick={() => setActiveTab('media')}
        >
          <FiImage />
          Media Library
        </button>
        <button 
          className={activeTab === 'templates' ? 'active' : ''}
          onClick={() => setActiveTab('templates')}
        >
          <FiBookmark />
          Templates
        </button>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'media' && (
        <div className="media-library">
          {libraryData && libraryData.groupedImages.length > 0 ? (
            <div className="library-content">
              {libraryData.groupedImages.map((group, groupIndex) => (
                <div key={groupIndex} className="user-group">
                  <div className="user-header">
                    <div className="user-info">
                      <FiUser className="user-icon" />
                      <div>
                        <h3>{group.user.fullName}</h3>
                        <p>{group.user.email}</p>
                        {group.user.company && <p className="company">{group.user.company}</p>}
                      </div>
                    </div>
                    <div className="image-count">
                      {group.images.length} image{group.images.length !== 1 ? 's' : ''}
                    </div>
                  </div>
                  
                  <div className="images-grid">
                    {group.images.map((image) => (
                      <div key={image.id} className="media-card">
                        <div className="image-container">
                          <img 
                            src={`${config.api.baseURL}${image.url}`} 
                            alt={image.altText || image.originalName}
                            loading="lazy"
                          />
                          <div className="image-overlay">
                            <div className="image-actions">
                              {hasPermission('canViewLibrary') && (
                                <button 
                                  className="action-btn"
                                  onClick={() => copyImageUrl(image.url)}
                                  title="Copy URL"
                                >
                                  <FiCopy />
                                </button>
                              )}
                              {hasPermission('canViewLibrary') && (
                                <button 
                                  className="action-btn"
                                  onClick={() => {
                                    if (!hasPermission('canViewLibrary')) {
                                      showToast.error('You do not have permission to view library images');
                                      return;
                                    }
                                    window.open(`${config.api.baseURL}${image.url}`, '_blank');
                                  }}
                                  title="View Full Size"
                                >
                                  <FiEye />
                                </button>
                              )}
                              {hasPermission('canViewLibrary') && (
                                <button 
                                  className="action-btn"
                                  onClick={() => {
                                    if (!hasPermission('canViewLibrary')) {
                                      showToast.error('You do not have permission to download library images');
                                      return;
                                    }
                                    const link = document.createElement('a');
                                    link.href = `${config.api.baseURL}${image.url}`;
                                    link.download = image.originalName;
                                    link.click();
                                  }}
                                  title="Download"
                                >
                                  <FiDownload />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="image-info">
                          <h4>{image.originalName}</h4>
                          <p className="image-meta">
                            {formatFileSize(image.size)} • {image.width}x{image.height}
                          </p>
                          {image.tags && image.tags.length > 0 && (
                            <div className="image-tags">
                              {image.tags.slice(0, 3).map(tag => (
                                <span key={tag} className="tag">{tag}</span>
                              ))}
                              {image.tags.length > 3 && (
                                <span className="tag more">+{image.tags.length - 3}</span>
                              )}
                            </div>
                          )}
                          <p className="upload-date">
                            {new Date(image.uploadedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <FiImage className="empty-icon" />
              <h3>No images found</h3>
              <p>Upload images through the CDN to see them here</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'templates' && (
        <div className="templates-section">
          <div className="empty-state">
            <FiBookmark className="empty-icon" />
            <h3>No templates found</h3>
            <p>Templates will appear here when created</p>
          </div>
        </div>
      )}

      {activeTab === 'rows' && (
        <div className="rows-section">
          <div className="empty-state">
            <FiFilter className="empty-icon" />
            <h3>No rows found</h3>
            <p>Row components will appear here when created</p>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}

export default Library; 